import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Brain, Pencil, Trash2, Check, X } from "lucide-react";

const CATEGORY_LABELS = {
  health: "Health",
  relationships: "People & relationships",
  finances: "Money",
  work: "Work",
  events: "Life events",
  goals: "Goals",
  preferences: "Preferences",
  other: "Other",
};

export default function MemoryPanel() {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/memories");
      setMemories(data.memories || []);
    } catch (err) {
      // silent — panel just stays empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (m) => {
    setEditingId(m.id);
    setDraft(m.content);
  };

  const saveEdit = async (id) => {
    const content = draft.trim();
    if (!content) return;
    try {
      const { data } = await api.patch(`/memories/${id}`, { content });
      setMemories((ms) => ms.map((m) => (m.id === id ? data.memory : m)));
      setEditingId(null);
      toast.success("Updated");
    } catch (err) {
      toast.error("Couldn't update");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/memories/${id}`);
      setMemories((ms) => ms.filter((m) => m.id !== id));
      toast.success("Forgotten");
    } catch (err) {
      toast.error("Couldn't delete");
    }
  };

  const grouped = memories.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});

  return (
    <section
      className="mt-6 bg-white rounded-3xl p-8 border border-slate-100 shadow-sm"
      data-testid="memory-panel"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#0E1417] text-[#3FC1B7] flex items-center justify-center shrink-0">
          <Brain size={20} />
        </div>
        <div>
          <h2 className="font-display text-2xl text-slate-800">What Zaf remembers about you</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Zaf builds this quietly as you talk, so it can be a real friend — not start over every time.
            Edit or delete anything. It's yours.
          </p>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <p className="text-slate-400 text-sm" data-testid="memory-loading">Loading…</p>
        ) : memories.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 px-5 py-8 text-center" data-testid="memory-empty">
            <p className="text-slate-500 text-[15px]">
              Nothing yet — the more you chat with Zaf, the more it learns about you and the people and
              things that matter.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(CATEGORY_LABELS)
              .filter((c) => grouped[c]?.length)
              .map((cat) => (
                <div key={cat} data-testid={`memory-group-${cat}`}>
                  <p className="uppercase tracking-[0.15em] text-xs font-semibold text-[#1F7B75] mb-2.5">
                    {CATEGORY_LABELS[cat]}
                  </p>
                  <div className="space-y-2.5">
                    {grouped[cat].map((m) => (
                      <div
                        key={m.id}
                        data-testid={`memory-item-${m.id}`}
                        className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-300 transition-colors"
                      >
                        {editingId === m.id ? (
                          <>
                            <textarea
                              value={draft}
                              onChange={(e) => setDraft(e.target.value)}
                              rows={2}
                              autoFocus
                              data-testid={`memory-edit-input-${m.id}`}
                              className="flex-1 text-[15px] text-slate-800 border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#3FC1B7]/40"
                            />
                            <div className="flex items-center gap-1.5 pt-0.5">
                              <button
                                type="button"
                                onClick={() => saveEdit(m.id)}
                                data-testid={`memory-save-${m.id}`}
                                className="w-8 h-8 rounded-lg bg-[#2E9D96] hover:bg-[#38B3AB] text-white flex items-center justify-center transition-colors"
                                aria-label="Save memory"
                              >
                                <Check size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                data-testid={`memory-cancel-${m.id}`}
                                className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
                                aria-label="Cancel"
                              >
                                <X size={15} />
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="flex-1 text-[15px] text-slate-700 leading-snug">{m.content}</p>
                            <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => startEdit(m)}
                                data-testid={`memory-edit-${m.id}`}
                                className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center transition-colors"
                                aria-label="Edit memory"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(m.id)}
                                data-testid={`memory-delete-${m.id}`}
                                className="w-8 h-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center transition-colors"
                                aria-label="Delete memory"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </section>
  );
}
